<template>
  <div v-if="evidence.length > 0">
    <!-- EHR Evidence (Could be closed) -->
    <div v-if="ehrEvidence.length > 0" class="pt-3 border-t border-gray-100 mb-4">
      <div class="text-xs font-semibold text-green-600 uppercase tracking-wider mb-1.5">
        <FontAwesomeIcon :icon="['fas', 'flask-vial']" class="mr-1" />
        EHR Evidence · Could Be Closed
      </div>
      <div class="space-y-1.5">
        <div v-for="(ev, idx) in ehrEvidence" :key="'ehr-'+idx"
             class="py-1.5 px-2.5 bg-green-50 rounded border border-green-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="text-xs font-medium text-gray-800">{{ formatType(ev.type) }}</span>
              <span class="text-[10px] text-green-600 font-medium">EHR</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-xs font-semibold" :class="valueClass(ev)">{{ shortValue(ev) }}</span>
              <span class="text-[10px] text-gray-500">{{ formatDate(ev.date) }}</span>
            </div>
          </div>
          <div v-if="isLongValue(ev) && expandedRows['ehr-'+idx]" class="mt-1.5 text-[10px] text-gray-600 bg-white rounded p-1.5 border border-green-100 whitespace-pre-wrap">{{ ev.value }}</div>
          <button v-if="isLongValue(ev)"
                  @click="toggleRow('ehr-'+idx)"
                  class="mt-0.5 text-[10px] text-green-600 hover:text-green-800">
            {{ expandedRows['ehr-'+idx] ? 'Hide' : 'Show details' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Claims Evidence (Probably closed) -->
    <div v-if="claimsEvidence.length > 0" class="pt-3 border-t border-gray-100 mb-4">
      <div class="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1.5">
        <FontAwesomeIcon :icon="['fas', 'file-invoice']" class="mr-1" />
        Claims Evidence · Probably Closed
      </div>
      <div class="space-y-1.5">
        <div v-for="(ev, idx) in claimsEvidence" :key="'clm-'+idx"
             class="py-1.5 px-2.5 bg-blue-50 rounded border border-blue-100">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="text-xs font-medium text-gray-800">{{ formatType(ev.type) }}</span>
              <span class="text-[10px] text-blue-600 font-medium">{{ formatSource(ev.source) }}</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-xs font-semibold" :class="valueClass(ev)">{{ shortValue(ev) }}</span>
              <span class="text-[10px] text-gray-500">{{ formatDate(ev.date) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="loading" class="pt-3 border-t border-gray-100">
    <div class="flex items-center py-2">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin text-gray-400 mr-2 text-xs" />
      <span class="text-xs text-gray-400">Loading evidence...</span>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const TYPE_LABELS = {
  'BP_SYSTOLIC': 'BP Systolic', 'BP_DIASTOLIC': 'BP Diastolic',
  'VITALS.BLOODPRESSURE.SYSTOLIC': 'BP Systolic', 'VITALS.BLOODPRESSURE.DIASTOLIC': 'BP Diastolic',
  'BP_CPTII_CONTROLLED': 'BP CPT II (Controlled)',
  'HBA1C_CPTII': 'HbA1c CPT II (Controlled)',
  'BMI': 'BMI', 'VITALS.BMI': 'BMI', 'WEIGHT_LBS': 'Weight', 'VITALS.WEIGHT': 'Weight',
  'PHQ9': 'PHQ-9', 'PHQ2': 'PHQ-2', 'DEPRESSION_SCREEN': 'Depression Screen',
  'TOBACCO': 'Tobacco Screen', 'FALL_RISK': 'Fall Risk Assessment',
  'HBA1C': 'HbA1c', 'EGFR': 'eGFR', 'UACR': 'uACR',
  'FOBT_FIT': 'FOBT/FIT', 'PAP_SMEAR': 'PAP Smear', 'HPV': 'HPV Test',
  'MAMMOGRAPHY': 'Mammography', 'COLONOSCOPY': 'Colonoscopy',
  'EYE_EXAM': 'Eye Exam', 'RETINAL_IMAGING': 'Retinal Imaging',
  'EYE_EXAM_CPTII': 'Eye Exam', 'CHLAMYDIA_TEST': 'Chlamydia Test',
  'FALL_RISK_SCREEN': 'Fall Risk Screen', 'FUNCTIONAL_STATUS': 'Functional Status',
  'PAIN_ASSESSMENT': 'Pain Assessment', 'MED_REVIEW': 'Medication Review'
}

const SHORT_VALUE_MAX = 30

export default {
  name: 'Clinical_Evidence_Widget',
  components: { FontAwesomeIcon },

  props: {
    patientIdentifier: {
      type: String,
      required: true
    },
    gap: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      fetchedEvidence: null,
      expandedRows: {}
    }
  },

  computed: {
    evidence() {
      if (this.gap.clinical_evidence && this.gap.clinical_evidence.length > 0) {
        return this.gap.clinical_evidence
      }
      return this.fetchedEvidence || []
    },
    ehrEvidence() {
      return this.evidence.filter(ev => !this.isClaimsSource(ev.source))
    },
    claimsEvidence() {
      // Only compliant claims can be closure evidence. For CBP, both a
      // compliant systolic and diastolic CPT II code are required (REQ 6209).
      const NON_COMPLIANT_CPTII = new Set(['3077F', '3080F', '3046F']);
      const claims = this.evidence.filter(ev => {
        if (!this.isClaimsSource(ev.source)) return false;
        if (ev.type?.includes('UNCONTROLLED') || NON_COMPLIANT_CPTII.has(ev.value)) return false;
        return true;
      });
      const cbp = claims.filter(ev => ev.quality_measure === 'CBP');
      const hasSystolic = cbp.some(ev => ['3074F', '3075F'].includes(ev.value));
      const hasDiastolic = cbp.some(ev => ['3078F', '3079F'].includes(ev.value));
      return claims.filter(ev => ev.quality_measure !== 'CBP' || (hasSystolic && hasDiastolic));
    }
  },

  watch: {
    patientIdentifier: {
      immediate: true,
      handler() { this.loadIfNeeded() }
    },
    'gap.id': {
      handler() {
        this.expandedRows = {}
        this.loadIfNeeded()
      }
    }
  },

  methods: {
    async loadIfNeeded() {
      if (this.gap.clinical_evidence !== undefined) return
      if (!this.patientIdentifier || !this.gap.measure_name) return

      this.loading = true
      try {
        const response = await this.$axios.post('/api/quality/clinical-evidence/', {
          patient_identifier: this.patientIdentifier
        })
        const allEvidence = response.data.evidence || []
        const measureCode = this.gap.measure_id
        this.fetchedEvidence = allEvidence
          .filter(g => g.quality_measure === measureCode)
          .flatMap(g => g.evidence)
      } catch (error) {
        console.error('Error loading clinical evidence:', error)
      } finally {
        this.loading = false
      }
    },

    formatType(type) {
      return TYPE_LABELS[type] || type
    },

    isClaimsSource(source) {
      if (!source) return false
      const PAYER_SOURCES = ['AETNA', 'ANTHEM', 'CIGNA', 'EMBLEM', 'UHC', 'FIDELIS']
      return source.startsWith('CLAIMS:') || PAYER_SOURCES.includes(source.toUpperCase())
    },

    formatSource(source) {
      if (!source) return 'EHR'
      if (source.startsWith('CLAIMS:')) return source.replace('CLAIMS:', '')
      return source
    },

    shortValue(ev) {
      if (ev.numeric !== null && ev.numeric !== undefined) {
        return String(ev.numeric)
      }
      const val = ev.value || 'Completed'
      if (val.length <= SHORT_VALUE_MAX) return val
      return val.substring(0, SHORT_VALUE_MAX) + '…'
    },

    isLongValue(ev) {
      return ev.value && ev.value.length > SHORT_VALUE_MAX
    },

    toggleRow(key) {
      this.expandedRows = { ...this.expandedRows, [key]: !this.expandedRows[key] }
    },

    valueClass(ev) {
      const qm = ev.quality_measure
      const n = ev.numeric
      if (n === null || n === undefined) return 'text-gray-700'
      if (qm === 'CBP' && ev.type?.includes('SYSTOLIC')) return n < 140 ? 'text-green-600' : 'text-red-600'
      if (qm === 'CBP' && ev.type?.includes('DIASTOLIC')) return n < 90 ? 'text-green-600' : 'text-red-600'
      if (qm === 'HBD') return n < 8 ? 'text-green-600' : n < 9 ? 'text-amber-600' : 'text-red-600'
      if (qm === 'KED' && ev.type === 'EGFR') return n >= 60 ? 'text-green-600' : n >= 30 ? 'text-amber-600' : 'text-red-600'
      if (qm === 'DSF' && ev.type === 'PHQ9') return n < 5 ? 'text-green-600' : n < 10 ? 'text-amber-600' : 'text-red-600'
      return 'text-gray-700'
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }
  }
}
</script>
